import React from 'react';
import { Box, Card, CardContent, CardActions, Typography, Button, Stack, type Theme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ReportProblem } from '@mui/icons-material';
import { paths } from '@/paths';
import { Logo } from '@/components/core/v2/logo';

interface ErrorBoundaryFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({ resetErrorBoundary }) => {
  const navigate = useNavigate();

  const handleContactSupport = () => {
    // You can implement your support contact logic here
    // For example: opening a support ticket modal, mailto link, or chat widget
    window.open('mailto:support@firstequityproperty.com?subject=Application Error Report', '_blank');
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: (theme: Theme) => theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Card
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 480,
          borderRadius: 3,
          bgcolor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack alignItems="center" spacing={3} textAlign="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'error.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <ReportProblem sx={{ fontSize: 40, color: 'error.main' }} />
            </Box>
            
            <Box>
              <Typography fontWeight={600} sx={{ mb: 1 }} variant="h5">
                Oops! Something went wrong
              </Typography>
              <Typography color="text.secondary" variant="body1">
                We're experiencing a temporary issue. Please try refreshing the page or contact our support team if the problem persists.
              </Typography>
            </Box>
            
            <Logo height={32} />
          </Stack>
        </CardContent>
        
        <CardActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%', alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', justifyContent: 'center' }}
          >
            <Button
              onClick={resetErrorBoundary}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
              variant="contained"
            >
              Try Again
            </Button>
            <Button
              onClick={() => { navigate(paths.home); }}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
              variant="outlined"
            >
              Go Home
            </Button>
            <Button
              color="inherit"
              onClick={handleContactSupport}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
              variant="text"
            >
              Contact Support
            </Button>
          </Stack>
        </CardActions>
      </Card>
    </Box>
  );
};

export default ErrorBoundaryFallback;
